import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Info, UserPlus, X } from "lucide-react";
import { resolveInviteErrorMessage } from "../lib/settingsErrorMessages";
import { track } from "../lib/usageTracker";

/**
 * Issue #530 — the coach's "Add Player" entry point, the half of #381
 * shape 1 that #518/#506 built the API for and no UI ever called: nothing
 * in the frontend posted `player_email` until this card existed, so a
 * coach could only ever invite a player with curl.
 *
 * PLACEMENT: the top of coach Settings — Martin's #459 fourth bullet asked
 * for "a shortcut button at the top", verbatim, so this card mounts ABOVE
 * the Account & profile card for the coach role only (Settings.jsx states
 * the section-order exception there). ALWAYS OPEN, not a #475-style fold,
 * deliberately: a page-head action that arrives folded is not a shortcut,
 * it is a hiding place — the fold contract exists to keep long
 * accumulating sections from costing a page, and this card is an entry
 * point plus a short bounded list (the pending cap is 5 server-side).
 * Because it does not fold it is registered in NO harness table and does
 * not join the SectionCard census (identityForm.test.js counts collapsible
 * call sites only).
 *
 * A contained component for the same reason CoachAccessInviteActions.jsx
 * is one: Settings.jsx composes sections and owns none of their forms
 * (#456), so this keeps #530's footprint there to one import and one
 * role-gated mount.
 *
 * ENUMERATION POSTURE (#506, adversarially reviewed — do NOT "fix" this):
 * `POST /coach-links {player_email}` returns the byte-identical
 * `COACH_LINK_ACK` whether the email matched a player account, a
 * non-player account, or nobody. That is deliberate anti-enumeration (the
 * same doctrine as a password reset's non-committal wording), so this
 * card shows the server's `detail` AS-IS and must never grow a "user not
 * found" / "invite delivered" state, a spinner-then-checkmark that differs
 * by outcome, or any other client-side hint about whether an account
 * exists. If you are here to add one, read routes/coachLinks.ts's
 * ENUMERATION RISK header first — the absence of that state is the
 * feature. The wording also deliberately does not promise an EMAIL went
 * out: a re-invite inside the server's 5-minute cooldown returns the same
 * ACK and sends nothing (#506 F-SPAM).
 *
 * THE OUTBOUND LIST is the complement of CoachPortal's pending-invites
 * card: that card filters to `initiated_by !== "coach"` (#506 — inbound
 * rows are the coach's decisions), and until #530 the rows it filtered OUT
 * rendered nowhere, so a coach got no confirmation an invite existed at
 * all. Here they render with WITHDRAW (the same `DELETE /:id` every other
 * exit from a pairing uses — decline and revoke are one verb server-side).
 *
 * NAME MASKING (#506): a coach-initiated `invited` row is served to its
 * coach with `player_full_name` null until the player accepts — the player
 * has consented to nothing yet, and a name here would upgrade the accepted
 * existence oracle into a PII harvest. On accept the row goes `active` and
 * leaves this list for the roster. So a row in this list has NO name by
 * design: it renders a neutral label, and must never fetch, guess, or
 * invent one. (The API does not echo the invited email back either, so the
 * label is the whole row identity — the coach knows which addresses they
 * invited.)
 *
 * `embedded` (issue #755, same name/shape as `PlayerProfile.jsx`'s own
 * `embedded` prop, built for exactly this reuse case): the coach set-up
 * screen's drawing (`CoachSetUp.dc.html`) puts this form inside ITS OWN
 * bordered "Invite your first player · Optional" section with a "Skip —
 * invite later" link — a second, nested card-inside-a-card would not
 * match it. `embedded` skips this file's own `<Card>`/heading/prose (the
 * caller supplies its own chrome) and renders only the form + outbound
 * list — the SAME state, the SAME two testids
 * (`settings-add-player-email`/`settings-add-player-send`), the SAME
 * `POST /coach-links` call and the SAME uniform acknowledgement. Settings
 * calls this with no `embedded` prop at all, so its rendering is
 * byte-for-byte unchanged.
 *
 * `kitType` (issue #830) — a second, independent prop, default `false`.
 * `CoachSetUp.jsx` is the only caller that sets it (alongside `embedded`,
 * which it also passes); `Settings.jsx`'s own call site,
 * `<AddPlayerCard />` (pinned verbatim by `AddPlayerCard.test.js`), sets
 * neither. `false` renders exactly what this file always has: shadcn
 * `Input`/`Button`, Tailwind `text-base`/`text-sm`, unmodified — #830's
 * own finding is that BOTH of `/coach-setup`'s two moving marked elements
 * trace here, not to anything `CoachSetUp.jsx` owns directly. `true`
 * swaps those two rem-scaled sizes (and the outbound-invite list's own
 * `text-sm`/`text-xs`, for the same reason — a coach who sends an invite
 * on `/coach-setup` and only then would see that list must not find the
 * type ramp's fixed-px claim was only true for the form above it) for
 * fixed px, using the SAME `--nw-*` tokens `CoachSetUp.jsx` already uses
 * for its own text. `ui/input.jsx` and `ui/button.jsx`'s own base classes
 * are untouched — every `<Input>`/`<Button>` elsewhere in the app must not
 * move — so the override happens at each call site below via INLINE
 * `style` alone (PR review F3: an earlier version of this comment claimed
 * `cn`'s `twMerge` replaced a later `text-*` class over the base's; no
 * such class is added anywhere below). `nw-kit-input` (the email input's
 * one added class, matching `Field.jsx`'s own kit-input pattern) supplies
 * only the shared `::placeholder` colour rule — it carries no `font-size`
 * of its own. The actual override is `style.fontSize`/`style.lineHeight`,
 * which wins on CSS SPECIFICITY (an inline style always beats a class,
 * `md:`-prefixed or not) over whatever `text-base`/`text-sm` the base
 * classes still apply underneath — not by replacing or de-duplicating
 * those classes in the rendered `className` string at all.
 *
 * AUDIT G6 (#869), fix round (D173 §G6, D173.C2). Two findings, both
 * `kitType`-scoped, both leave `embedded={false}` / `kitType={false}`
 * (Settings' own call site) untouched:
 *
 * 1. THE EMAIL BOX COLLAPSED TO ~31px (`AddPlayerCard.jsx:156-162`,
 *    pre-fix). The row was `flex flex-col sm:flex-row`, and the input
 *    carried `flex-1` unconditionally. At <sm (the only width this embed
 *    is ever shown at — CoachSetUp is a 390px artboard) the row's main
 *    axis is vertical, so `flex-1`'s `flex-basis: 0%` becomes the input's
 *    hypothetical MAIN (height) size — 0 — and it only grows by however
 *    much free space the (auto-height) column has to hand out, which is
 *    none: the `h-11` height class loses to an explicit `flex-basis`,
 *    same as a `width` losing to a horizontal `flex-basis`. The box
 *    rendered at its own min-content height (~31px) instead of 46px. Fix:
 *    under `kitType` the input is no longer a flex sibling of the button
 *    at all — its own row, `width: 100%`, `height: var(--nw-size-control)`
 *    inline (wins on specificity over the untouched base classes, same
 *    reasoning as every other inline override in this file), background
 *    and border set to the kit's white/`--nw-line` explicitly (the base
 *    `Input` is `bg-transparent border-input`, which is NOT the drawn
 *    white/`#E2E8F0` — `CoachSetUp.dc.html:92`).
 * 2. SEND WAS FULL-WIDTH AND GREY-WHEN-EMPTY (`AddPlayerCard.jsx:184-194`,
 *    pre-fix). `sm:w-auto` only takes effect at >=sm, so at 390 the
 *    button stretched full width against the drawn compact, inline size
 *    (`CoachSetUp.dc.html:94`); and the native `disabled` attribute
 *    (`disabled={sending || !email.trim()}`) triggered the base button's
 *    `disabled:opacity-50`, which is not what the artboard draws — it
 *    draws Send navy at full strength even with nothing typed (D173.C2,
 *    "looks as drawn … but is `aria-disabled`", the same pattern D172.S1
 *    gives set-up's Continue and PR #920's `squad-team-selection` already
 *    uses). Fix: under `kitType`, Send sits in its own row alongside the
 *    caller's `sendRowExtra` slot (below), inline-sized, and the native
 *    `disabled` attribute is reserved for the in-flight `sending` state
 *    only (so the double-fire guard the unit test pins,
 *    `disabled={sending || !email.trim()}`, stays literally true on the
 *    UNCHANGED non-`kitType` branch a few lines down — this branch is a
 *    SEPARATE element, not an edit to that one). The empty-box case is
 *    `aria-disabled` instead: still navy, still focusable, and a click
 *    focuses the email box rather than firing the request — `sendInvite`
 *    itself still no-ops on an empty trimmed value either way, so this is
 *    belt-and-suspenders, not the only guard.
 *
 * `sendRowExtra` (a third prop, default `null`) — CoachSetUp's own "Skip —
 * invite later" link, rendered in the SAME row as Send
 * (`justify-content: space-between`, `CoachSetUp.dc.html:93-96`) rather
 * than on a row of its own below the card. This component still owns
 * nothing about what that link does (`continueToPortal`, CoachSetUp's own
 * handler) or its testid (`coach-setup-skip-invite`, also CoachSetUp's) —
 * it only owns where it sits, because only this component knows where
 * Send's row is. `null` (Settings' call site, and any non-`kitType` use)
 * renders nothing extra; the row's `justify-content: space-between` then
 * simply has one child, which is harmless and untested (Settings never
 * sets `kitType`, so it never reaches this branch at all).
 *
 * `appearance` (a fourth prop, default `undefined`) — issue #976, Settings
 * B, #989 round 2 fix round. Deliberately SEPARATE from `kitType`: this
 * card is shared with `CoachSetUp.jsx`, which sets `kitType` (for the type
 * ramp) but was never part of #976's own scope, so gating these three
 * D55/D57/D195 fixes on `kitType` alone would have silently changed
 * `/coach-setup` too. Only `SettingsScreen.jsx`'s coach variant sets
 * `appearance="r5"`; `CoachSetUp.jsx` sets neither, so it is byte-identical
 * before and after this round. `appearance="r5"`:
 *   - F3/D195: the outbound "Invite pending" pill goes navy on `#E8F1FB`,
 *     as drawn (`SettingsCoach.dc.html:56`) — the shipped gold tint
 *     (`#D4A01720`/`#8a6a10`) was never drawn, and R-003 counts it (the
 *     alpha channel this file's own `#D4A01720` carries still parses as
 *     gold to a computed-style sweep that only reads R/G/B);
 *   - N2/D55, corrected by #989 round 3's N1: withdraw takes D55's
 *     alert-outlined shape, but stays the drawn 40px ICON-ONLY `X` trigger
 *     — round 2's own LABELLED "Withdraw invite" button squeezed the row
 *     at 390px and truncated "Invited player"/"Waiting for them…" to
 *     "Inv…"/"Wait…". `aria-label="Withdraw invite"` carries the name a
 *     visible label would have, same testid and handler as the shipped
 *     trigger it replaces;
 *   - F4/D57: the send-invite refusal (`settings-add-player-error`, the
 *     drawn words: "The server's reason shows here when it gives one.")
 *     and a withdraw failure (today's bare `toast.error("Failed")`) both
 *     render on the page via the shared `AlertCallout` shape — never a
 *     toast. A successful send/withdraw still toasts (D109) — untouched.
 */
const ALERT = "#B42318";

/** D57's on-page failure shape (round 2 review, S-F1) — kept as its own
 * local copy, matching this codebase's stated preference over a shared
 * import for a ten-line block a handful of files use. */
function AlertCallout({ children, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        padding: "10px 12px",
        background: "#F1F5F9",
        borderLeft: `3px solid ${ALERT}`,
        borderRadius: 10,
        fontSize: 13,
        lineHeight: 1.5,
        color: "#0F172A",
      }}
    >
      <span style={{ display: "inline-flex", flexShrink: 0, marginTop: 1 }}>
        <Info size={16} color={ALERT} />
      </span>
      <span>{children}</span>
    </div>
  );
}

export default function AddPlayerCard({ embedded = false, kitType = false, sendRowExtra = null, appearance } = {}) {
  const r5 = appearance === "r5";
  const [outbound, setOutbound] = useState([]);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [busyId, setBusyId] = useState(null);
  // D57 (#989 round 2, F4) — only read/set under r5; the shipped and
  // CoachSetUp (kitType-only) renders still toast and never touch these.
  const [sendError, setSendError] = useState("");
  const [withdrawError, setWithdrawError] = useState({});
  const emailInputRef = useRef(null);

  // Outbound pending only: `invited` rows THIS coach initiated. Active
  // links are the roster (CoachPortal), inbound invited rows are the
  // portal's accept/decline card — this list is exactly what neither of
  // those surfaces will show (#506's filter, complemented).
  const loadOutbound = () =>
    api
      .get("/coach-links")
      .then((r) => setOutbound(r.data.filter((l) => l.status === "invited" && l.initiated_by === "coach")))
      .catch(() => setOutbound([]));

  useEffect(() => { loadOutbound(); }, []);

  const sendInvite = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    setSending(true);
    setSendError("");
    try {
      const { data } = await api.post("/coach-links", { player_email: trimmed });
      // AS-IS, deliberately generic — see the enumeration note above and
      // routes/coachLinks.ts. This UI must never claim to know whether the
      // email matched a player account, or whether an email was sent.
      track("coach.invite_sent");
      toast.success(data.detail);
      setEmail("");
      loadOutbound();
    } catch (e) {
      // Real errors only reach here (invalid email 400, self-invite 400,
      // pending-cap 429) — none of them outcome-dependent on the target.
      // D57 (#989 round 2, F4): on the page under r5, no toast. The
      // kitType-only leg (coach set-up) keeps the toast, but — issue #984
      // — now computes it with `resolveInviteErrorMessage` too, the same
      // resolver r5 and `CoachAccessCardNew.jsx` already use:
      // `formatApiErrorDetail(null)` never returns a falsy value, so the
      // old `formatApiErrorDetail(...) || "Could not send invite"` could
      // never reach its own fallback. The shipped (non-kitType) leg below
      // keeps that dead pattern, byte-identical — it retires with the
      // `settings` flag, not fixed here (#984's explicit scope).
      if (r5) {
        setSendError(resolveInviteErrorMessage(e.response?.data?.detail));
      } else if (kitType) {
        toast.error(resolveInviteErrorMessage(e.response?.data?.detail));
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send invite");
      }
    }
    setSending(false);
  };

  const withdraw = async (link) => {
    setBusyId(link.id);
    setWithdrawError((prev) => ({ ...prev, [link.id]: null }));
    try {
      await api.delete(`/coach-links/${link.id}`);
      toast.success("Invite withdrawn");
      loadOutbound();
    } catch {
      // D57 (#989 round 2, F4): on the page under r5, per row, no toast.
      if (r5) setWithdrawError((prev) => ({ ...prev, [link.id]: "Failed" }));
      else toast.error("Failed");
    }
    setBusyId(null);
  };

  // D173.C2 / G6.2: an empty box still looks navy at full strength — no
  // native `disabled`, so no `disabled:opacity-50` from the base button —
  // and a tap goes to the box, not to the (still-guarded) request.
  const onSendClick = () => {
    if (!email.trim()) {
      emailInputRef.current?.focus();
      return;
    }
    sendInvite();
  };

  const emailAndSendRow = kitType ? (
    // G6.1: the input is no longer `flex-1` beside the button (see the
    // docstring above for why that collapsed it) — its own row, full
    // width, fixed 46px. Below it, Send and the caller's `sendRowExtra`
    // share one `space-between` row, matching CoachSetUp.dc.html:93-96.
    <div className="flex flex-col" style={{ gap: "var(--nw-space-grid)" }}>
      <Input
        ref={emailInputRef}
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="player@example.com"
        className="nw-kit-input"
        style={{
          width: "100%",
          height: "var(--nw-size-control)",
          borderRadius: "var(--nw-radius-control)",
          border: "1px solid var(--nw-line)",
          background: "var(--nw-surface)",
          padding: "0 12px",
          fontSize: 15,
          lineHeight: 1.4,
          color: "var(--nw-ink)",
          boxShadow: "none",
        }}
        data-testid="settings-add-player-email"
        aria-label="Player's email address"
        onKeyDown={(e) => { if (e.key === "Enter") sendInvite(); }}
      />
      <div className="flex items-center justify-between">
        <Button
          type="button"
          onClick={onSendClick}
          disabled={sending}
          aria-disabled={sending || !email.trim()}
          className={r5 ? "w-auto" : "group w-auto shadow-none outline-none focus-visible:ring-0"}
          style={
            r5
              ? {
                  // #1135 (F5, D259): the board's outline under r5; D195 only forbids gold.
                  background: "var(--nw-surface)",
                  color: "var(--nw-navy)",
                  border: "1px solid var(--nw-line-strong)",
                  fontSize: 14,
                  fontWeight: 600,
                  lineHeight: 1.4,
                  minHeight: 44,
                  height: "auto",
                  padding: "0 16px",
                  borderRadius: "var(--nw-radius-control)",
                }
              : {
                  // #1394 (D221 G4, D354): off r5 (CoachSetUp) the button is a transparent 44px
                  // tap box and the 40px navy button is drawn inside it. The margin pulls the
                  // margin box back to the button's own 40px, so the row keeps its height and
                  // the button stays where it was drawn. The ring on a keyboard-focused button
                  // is drawn on the navy button (`group-focus-visible`), not the invisible box.
                  background: "transparent",
                  border: "none",
                  minHeight: "var(--nw-size-tap)",
                  height: "auto",
                  marginBlock: "calc((40px - var(--nw-size-tap)) / 2)",
                  padding: 0,
                  borderRadius: "var(--nw-radius-control)",
                }
          }
          data-testid="settings-add-player-send"
        >
          {r5 ? (
            sending ? "Sending…" : "Send invite"
          ) : (
            <span
              className="shadow group-focus-visible:ring-2 group-focus-visible:ring-[#0B2E59] group-focus-visible:ring-offset-2"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 40,
                padding: "0 16px",
                background: "var(--nw-navy)",
                color: "#FFFFFF",
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.4,
                borderRadius: "var(--nw-radius-control)",
              }}
            >
              {sending ? "Sending…" : "Send invite"}
            </span>
          )}
        </Button>
        {sendRowExtra}
      </div>
    </div>
  ) : (
    <div className="flex flex-col sm:flex-row gap-2">
      <Input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="player@example.com"
        className="h-11 flex-1"
        data-testid="settings-add-player-email"
        // Review round 3, final narrow round (A3), F9: no visible label
        // sibling either caller draws (both the standalone Settings
        // card and CoachSetUp's own `embedded` chrome supply their own
        // heading text, never a per-field one) — an aria-label carries
        // the name instead, same shape as `log-search`'s own fix.
        aria-label="Player's email address"
        onKeyDown={(e) => { if (e.key === "Enter") sendInvite(); }}
      />
      <Button
        onClick={sendInvite}
        disabled={sending || !email.trim()}
        className="h-11 sm:w-auto"
        style={{ background: "#0B2E59" }}
        data-testid="settings-add-player-send"
      >
        {sending ? "Sending…" : "Send invite"}
      </Button>
    </div>
  );

  const form = (
    <>
      {emailAndSendRow}

      {/* D57 (#989 round 2, F4): a send-invite refusal renders here under
          r5, never a toast. Drawn words: "The server's reason shows here
          when it gives one." (SettingsCoach.dc.html's own invite-failed
          state) — formatApiErrorDetail already supplies that, falling back
          to "Could not send invite". */}
      {r5 && sendError && <AlertCallout testId="settings-add-player-error">{sendError}</AlertCallout>}

      {outbound.length > 0 && (
        <div className="space-y-2 pt-1" data-testid="settings-outbound-invites">
          {outbound.map((l) => {
            const nameBlock = (
              <div className="min-w-0">
                {/* Masked-name-safe on purpose — see the NAME MASKING note
                    above. `player_full_name` is null on every row this
                    list can hold; the fallback label is the render, not a
                    band-aid. */}
                <div
                  className={kitType ? "truncate" : "text-sm font-semibold text-slate-900 truncate"}
                  style={kitType ? { fontFamily: "'Manrope', sans-serif", fontSize: 15, lineHeight: 1.4, fontWeight: 700, color: "var(--nw-ink)" } : undefined}
                >
                  {l.player_full_name || "Invited player"}
                </div>
                <div
                  className={r5 ? "break-words" : kitType ? "truncate" : "text-xs text-slate-500 truncate"}
                  style={kitType ? { fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" } : undefined}
                  data-testid={r5 ? `settings-outbound-invite-waiting-${l.id}` : undefined}
                >
                  Waiting for them to accept or decline
                </div>
              </div>
            );
            const badge = (
              // F3/D195 (#989 round 2): navy on #E8F1FB under r5, as
              // drawn — the shipped gold tint (its alpha channel still
              // parses as gold to a computed-style sweep) is unchanged
              // off r5.
              <Badge
                className="text-[10px]"
                style={r5 ? { background: "#E8F1FB", color: "#0B2E59" } : { background: "#D4A01720", color: "#8a6a10" }}
                data-testid={`settings-outbound-invite-status-${l.id}`}
              >
                Invite pending
              </Badge>
            );

            if (r5) {
              // #989 round 2 (F4): a vertical stack (the name+badge+
              // withdraw row, then an optional AlertCallout below it) so
              // a withdraw failure has somewhere to sit — a SEPARATE
              // structure from the shipped single-row layout below, not
              // an edit to it.
              return (
                <div
                  key={l.id}
                  className="rounded-lg border border-slate-200 p-3 flex flex-col gap-2"
                  data-testid={`settings-outbound-invite-${l.id}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    {nameBlock}
                    <div className="flex items-center gap-2 shrink-0">
                      {badge}
                      {/* N1 (#989 round 3): the drawn 40px ICON-ONLY `X`
                          trigger, D55's alert-outlined colour — round 2's
                          LABELLED button squeezed this row at 390px and
                          truncated the name/status text beside it.
                          `aria-label` carries what the visible label used
                          to. Same testid and handler as the shipped
                          trigger below. */}
                      <button
                        type="button"
                        onClick={() => withdraw(l)}
                        disabled={busyId === l.id}
                        aria-label="Withdraw invite"
                        title="Withdraw invite"
                        style={{
                          width: 40,
                          height: 40,
                          flexShrink: 0,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: 10,
                          border: `1px solid ${ALERT}`,
                          background: "#FFFFFF",
                          color: ALERT,
                          cursor: busyId === l.id ? "not-allowed" : "pointer",
                          opacity: busyId === l.id ? 0.6 : 1,
                        }}
                        data-testid={`settings-outbound-invite-withdraw-${l.id}`}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                  {/* D57: a withdraw failure renders here, per row, never
                      a toast. */}
                  {withdrawError[l.id] && (
                    <AlertCallout testId={`settings-outbound-invite-withdraw-error-${l.id}`}>{withdrawError[l.id]}</AlertCallout>
                  )}
                </div>
              );
            }

            return (
              <div
                key={l.id}
                className="rounded-lg border border-slate-200 p-3 flex items-center justify-between gap-3"
                data-testid={`settings-outbound-invite-${l.id}`}
              >
                {nameBlock}
                <div className="flex items-center gap-2 shrink-0">
                  {badge}
                  <button
                    onClick={() => withdraw(l)}
                    disabled={busyId === l.id}
                    title="Withdraw invite"
                    className="h-11 w-11 sm:h-9 sm:w-9 flex items-center justify-center text-slate-400 hover:text-red-600 disabled:opacity-50"
                    data-testid={`settings-outbound-invite-withdraw-${l.id}`}
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  if (embedded) {
    return (
      <div
        className={r5 ? "flex flex-col" : "space-y-4"}
        style={r5 ? { gap: 10 } : undefined}
        data-testid="settings-add-player-card"
      >
        {form}
      </div>
    );
  }

  return (
    <Card className="border-slate-200" data-testid="settings-add-player-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#0B2E5915" }}>
            <UserPlus size={16} color="#0B2E59" />
          </div>
          <div>
            <h2 className="font-display font-bold text-lg text-slate-900">Add player</h2>
            <p className="text-sm text-slate-500">Invite a player by their account email. They'll see the invite in their Settings and choose whether to accept — you can't see any of their cricket data unless they do.</p>
          </div>
        </div>

        {form}
      </CardContent>
    </Card>
  );
}
